/** @jsxImportSource @emotion/react */
import React, { useState, ReactNode, useMemo } from 'react';
import MikeNotificationButton from './MikeNotificationButton';
import MikeNotificationGroups from './MikeNotificationGroups';
import { ClickAwayListener } from '@mui/material';
import MikeTopbarDropdown, { wrapperStyle } from '../mike-topbar-dropdown';
import { INotification } from './INotification';
import MikeTooltip from '../mike-tooltip';
import { IMikeTooltipPosition } from '../mike-tooltip/MikeTooltip';

interface IProps {
  /**
   * Notifications.
   */
  notifications: Array<INotification>;
  /**
   * href.
   */
  href?: string;
  /**
   * Link callback.
   */
  onLinkCallBack?: (arg0: INotification) => void; 
  /**
   * Custom content.
   */
  content?: ReactNode;
  /**
   * Tooltip title.
   * Use "" for no tooltip.
   */

  tooltipTitle?: string;
  /**
   * How to position the tooltip in relation to its target/anchor.
   * Structure is `{ top?: number, left?: number }`.
   */
  tooltipPosition?: IMikeTooltipPosition;
  /**
   * Custom title.
   */
  title?: string;
  /**
   * Custom 'no content' label.
   */
  noContentLabel?: string;
  /**
   * Custom render function.
   * To display the content of a notification item.
   */
  renderContent?: (notification: INotification) => React.ReactNode;
  /**
   * Custom render function.
   * To display the title of a notification item.
   */
  renderTitle?: (notification: INotification) => React.ReactNode;
}

/**
 * @name MikeNotificationContainer
 * @summary The main notification wrapper-component. Used to display generic notifications in the topbar.
 */
export const MikeNotificationContainer = (props: IProps) => {
  const {
    notifications,   
    href,
    onLinkCallBack,   
    content,
    tooltipTitle = 'Notifications',
    tooltipPosition,
    title = 'Notifications',
    noContentLabel = 'No notifications to show yet',
    renderContent,
    renderTitle,
  } = props;
  const ttPos = { left: -9, top: -1, ...tooltipPosition };
  const [showNotifications, setShowNotifications] = useState(false);
  
  const handleClickAway = () => {
    setShowNotifications(false)
  }

  const handleOnClick = () => {
    setShowNotifications(true)
  }

  const dotVisible = useMemo(() => {
    return !showNotifications && (notifications && notifications.length > 0)  ? true : false 
  },[showNotifications, notifications])

  // Only show tooltips when collapsed:
  const ttTitle = showNotifications ? '' : tooltipTitle;

  return (
    <div css={wrapperStyle}>
      <MikeTooltip title={ttTitle} arrow mikeposition={ttPos}>
        <MikeNotificationButton dotInvisible={!dotVisible} onClickCallBack={handleOnClick} />
      </MikeTooltip>
      {showNotifications && (
        <ClickAwayListener onClickAway={handleClickAway}>
          <div>
            <MikeTopbarDropdown>
              {content ? (
                content
              ) : (
                <MikeNotificationGroups
                  notifications={notifications}
                  href={href}
                  onLinkCallBack={onLinkCallBack}
                  title={title}
                  noContentLabel={noContentLabel}
                  renderContent={renderContent}
                  renderTitle={renderTitle}
                />
              )}
            </MikeTopbarDropdown>
          </div>
        </ClickAwayListener>
      )}
    </div>
  );
};

export default MikeNotificationContainer;
